import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isBefore, subDays } from 'date-fns';
import { MeApi } from 'src/api';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { getCurrentDate } from 'src/utils/dateTime';
import { DISPLAYED_ACTIVITIES } from 'src/apps/Activities/constants';
import { StatsResponse } from 'src/api/me/types';

const API_RELEASE_DATE = '2025-04-01';

const currentDate = getCurrentDate();

export const useActivitiesStats = (initialInterval: number) => {
  const languagePairId = getLanguagePairId();
  const [intervalLength, setIntervalLength] = useState<number>(initialInterval);

  const formatApiDate = (date: Date) => format(date, 'yyyy-MM-dd');

  const startDate = useMemo(() => subDays(currentDate, intervalLength - 1), [intervalLength]);

  const currentStatsStart = formatApiDate(startDate);
  const currentStatsEnd = formatApiDate(currentDate);
  const { data: currentStats } = useQuery({
    queryKey: ['stats', languagePairId, currentStatsStart, currentStatsEnd],
    queryFn: () =>
      MeApi.getStats(languagePairId as number, {
        startDate: currentStatsStart,
        endDate: currentStatsEnd,
      }),
    enabled: !!languagePairId,
  });

  const previousStatsEnd = useMemo(() => formatApiDate(subDays(startDate, 1)), [startDate]);
  const previousStatsStart = useMemo(
    () => formatApiDate(subDays(startDate, intervalLength)),
    [startDate, intervalLength],
  );
  const { data: previousStats } = useQuery({
    queryKey: ['stats', languagePairId, previousStatsStart, previousStatsEnd],
    queryFn: () =>
      MeApi.getStats(languagePairId as number, {
        startDate: previousStatsStart,
        endDate: previousStatsEnd,
      }),
    enabled: !!languagePairId,
  });

  const { data: languagePairInfo } = useQuery({
    queryKey: ['getUserLanguageInfo', languagePairId],
    queryFn: () => MeApi.getUserLanguageInfo(languagePairId as number),
    enabled: !!languagePairId,
  });

  const statsDifference = useMemo(() => {
    if (!previousStats || !currentStats) return undefined;

    const diff: Partial<StatsResponse> = {};

    DISPLAYED_ACTIVITIES.forEach(({ statsField }) => {
      diff[statsField] = currentStats[statsField] - previousStats[statsField];
    });

    return diff as StatsResponse;
  }, [previousStats, currentStats]);

  const isIncompleteData = !languagePairInfo
    ? undefined
    : isBefore(languagePairInfo.dateStarted, API_RELEASE_DATE) &&
      isBefore(previousStatsStart, API_RELEASE_DATE);

  return {
    intervalLength,
    setIntervalLength,
    intervalStartDate: startDate,
    intervalEndDate: currentDate,
    stats: currentStats,
    statsDifference,
    isIncompleteData,
  };
};
